import { useEffect, useState } from "react";
import moment from "moment";
import useModal from "../../hooks/modalHook";
import Card from "../../components/elements/Card";
import { IActivitySummary, IDateRange } from "../../resources/interfaces";
import useUsers from "../../hooks/UsersHook";
import { useQuery } from "@tanstack/react-query";
import { CEDI_SIGN, SERVER_DATE_FORMAT } from "../../resources/constants";
import DateRangeSelector from "../../components/dashboard/tabs/date-range-selector/DateRangeSelector";
import { SelectedDateRange } from "../../components/dashboard/tabs/date-range-selector/datePicker";
import { DATE_RANGE_TYPES, RECONCILIATION_STAGES } from "../../resources/enums";
import useUserAccount from "../../hooks/stateHooks/userAccountHook";
import Button from "../../components/form-control/Button";
import { GiTakeMyMoney } from "react-icons/gi";
import { IoWalletOutline } from "react-icons/io5";
import CediSign from "../../components/elements/CediSign";
import SubmitReconciliation from "../transactions/SubmitReconciliation";
import { numberWithCommas } from "../../utilities/miscFunctions";
import { Reconciliation } from "../../resources/types";
import ReconciliationsList from "../transactions/ReconciliationsList";
import useTransactions from "../../hooks/transactionsHook";
import RecentTransactions from "../transactions/RecentTransactions";
import AgentsLeaderboard from "./AgentsLeaderboard";

interface IProps {
    id?: string;
}

export default function AgentDashboard(props: IProps) {
    const { user, fetchUserById, loadingUsers, metrics, fetchUserMetrics, fetchUserReconciliationOutstanding, reconciliationOutstanding, fetchActivitySummary, activitySummary } = useUsers();
    const { fetchReconciliations, reconciliations } = useTransactions({ onTransactionAdded: () => { } })
    const { getProfile } = useUserAccount();
    const { ModalComponent, closeModal, updateModalStates } = useModal();

    const [dateRange, setDateRange] = useState<IDateRange>({
        from: moment().startOf("day").format(SERVER_DATE_FORMAT),
        to: moment().endOf("day").format(SERVER_DATE_FORMAT)
    });

    const userId = props.id ?? getProfile().id;

    useEffect(() => {
        if (userId) {
            fetchUserById(userId);
        }
    }, [userId])

    useEffect(() => {
        fetchUserReconciliationOutstanding(userId);
        fetchReconciliations({
            agentId: userId,
            status: RECONCILIATION_STAGES.PENDING
        })
        fetchActivitySummary();
    }, [props.id])

    const metricsCards = [
        {
            title: "Deposits",
            value: `${CEDI_SIGN} ${metrics.total_deposits_amount}`,
            sub: `${metrics?.total_deposits_count} Transactions`
        },
        {
            title: "Withdrawals",
            value: `${CEDI_SIGN} ${metrics.total_withdrawals_amount}`,
            sub: `${metrics?.total_withdrawals_count} Transactions`
        },
        {
            title: "Accounts Opened",
            value: metrics?.total_accounts
        },
        {
            title: "Customers Added",
            value: metrics?.total_customers
        },
        {
            title: "Customers Engaged",
            value: `${metrics?.total_customers_engaged ?? 0}`
        },
        {
            title: "Cash In Hand",
            value: `${CEDI_SIGN} ${metrics.total_cash}`,
            sub: `${metrics?.total_cash_count} Transactions`
        }
    ]

    const { data, isLoading, isStale, isFetching } = useQuery<any, Error>({
        queryKey: [
            'metrics',
            props.id,
            dateRange
        ],
        queryFn: () => fetchUserMetrics(props.id, dateRange.from, dateRange.to)
    });

    const submitReconciliation = () => {
        updateModalStates({
            show: true,
            children: <SubmitReconciliation
                amountExpected={reconciliationOutstanding?.total_amount_outstanding}
                transactions={reconciliationOutstanding.transactions_outstanding}
                onComplete={(reconciliation: Reconciliation) => {
                    fetchUserReconciliationOutstanding(userId);
                    fetchReconciliations({
                        agentId: userId,
                        status: RECONCILIATION_STAGES.PENDING
                    })
                    closeModal();
                }}
            />,
            title: "Submit Reconciliation"
        })
    }

    // if (loadingUsers) return <Loader message="Loading user details..." logoLoader />

    return (
        <div className="space-y-6" >
            <Card classes="shadow-sm border border-gray-100 p-4 !bg-primary-main dark:!bg-primary-dark-surface">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-200">
                            Outstanding Reconciliation
                        </p>

                        <h2 className="mt-2 text-3xl font-bold text-white">
                            <CediSign /> {reconciliationOutstanding?.total_amount_outstanding}
                        </h2>

                        <p className="mt-1 text-sm text-primary-secondary flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>

                            Awaiting reconciliation
                        </p>
                    </div>

                    <div className="h-14 w-14 rounded-xl bg-amber-100 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M17 9V7a5 5 0 00-10 0v2m-2 0h14l-1 11H6L5 9z" />
                        </svg>
                    </div>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-4">
                    <div className="rounded-xl bg-gray-50 dark:bg-primary-dark-secondary p-4 dark:border dark:border-primary-dark-border_active">
                        <div className="flex items-center gap-2 text-gray-500 dark:text-text-dark-secondary text-sm">
                            <GiTakeMyMoney className="w-4 h-4" />
                            Transactions
                        </div>

                        <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-text-dark-primary">
                            {reconciliationOutstanding.total_outstanding}
                        </div>
                    </div>

                    <div className="rounded-xl bg-gray-50 dark:bg-primary-dark-secondary p-4 dark:border dark:border-primary-dark-border_active">
                        <div className="flex items-center gap-2 text-gray-500 dark:text-text-dark-secondary text-sm">
                            <IoWalletOutline className="w-4 h-4" />
                            Average
                        </div>

                        <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-text-dark-primary">
                            {reconciliationOutstanding?.total_amount_outstanding > 0 ? <><CediSign /> {numberWithCommas((reconciliationOutstanding?.total_amount_outstanding / reconciliationOutstanding.total_outstanding).toFixed(2))}</> : 0
                            }
                        </div>
                    </div>
                </div>

                {((user?.isAgent() || (userId == getProfile().id)) && reconciliationOutstanding?.total_amount_outstanding > 0) && (
                    <div className="mt-6">
                        <Button
                            className="w-full md:w-[200px] rounded border-none bg-primary-secondary px-4 py-3 text-white font-medium hover:opacity-90 hover:text-white transition"
                            label="Submit Reconciliation"
                            onClick={submitReconciliation}
                        />

                    </div>
                )}
            </Card>

            {reconciliations.length > 0 && (
                <Card header={<h2 className="text-lg font-semibold mb-3 p-4">
                    Reconciliations Pending Approval
                </h2>}>
                    <ReconciliationsList
                        id={userId}
                        showHeader={false}
                        showSearch={false}
                        status={RECONCILIATION_STAGES.PENDING}
                    />
                </Card>
            )}

            {/* Filters */}
            < Card classes="p-4 overflow-visible" >
                {/* KPI Cards */}
                <DateRangeSelector
                    onChange={(date: SelectedDateRange) => {
                        if (date && date.to && date.from) {
                            setDateRange(date);
                        }
                    }
                    }
                    dateRangeType={DATE_RANGE_TYPES.TODAY}
                    className="md:w-[200px] rounded-lg mb-2"
                />
                <div className="grid grid-cols-2 xl:grid-cols-6 gap-4">

                    {metricsCards.map(card => (
                        <div
                            key={card.title}
                            className="bg-white dark:bg-primary-dark-secondary border border-gray-200 dark:border-primary-dark-border_active rounded-2xl p-5"
                        >
                            <p className="text-sm text-gray-500 dark:text-text-dark-secondary">
                                {card.title}
                            </p>

                            <h3 className="text-xl font-bold mt-2">
                                {card.value}
                            </h3>

                            {card.sub && (
                                <p className="text-sm text-gray-500 dark:text-text-dark-surface mt-1">
                                    {card.sub}
                                </p>
                            )}
                        </div>
                    ))}

                </div>
            </Card >

            <Card
                classes="bg-white border border-gray-200 rounded-2xl p-5"
                header={<h2 className="text-lg font-semibold mb-5">
                    Portfolio Summary
                </h2>}
            >

                <div className="space-y-4">

                    <div className="flex justify-between">
                        <span>Total Accounts</span>
                        <span className="font-semibold">{activitySummary.accounts.total}</span>
                    </div>

                    <div className="flex justify-between">
                        <span>Total Customers</span>
                        <span className="font-semibold">{activitySummary.customers.total}</span>
                    </div>

                    <div className="flex justify-between">
                        <span>Total Deposits</span>
                        <span className="font-semibold">{activitySummary.deposits.total}</span>
                    </div>

                    <div className="flex justify-between">
                        <span>Total Withdrawals</span>
                        <span className="font-semibold">{activitySummary.withdrawals.total}</span>
                    </div>

                </div>
            </Card>

            {/* Agents Leaderboard */}
            {user && user.branch_id && (
                <AgentsLeaderboard branchId={user?.branch_id} />
            )}

            {/* Recent Transactions */}
            {user && user.recentTransactions && (
                <RecentTransactions transactions={user?.recentTransactions} />
            )}
            <ModalComponent />
        </div >
    );
}

export const ACTIVITY_SUMMARY_INIT_DATA: IActivitySummary = {
    accounts: {
        today: 0,
        total: 0,
        balance: 0,
    },
    customers: {
        today: 0,
        total: 0
    },
    deposits: {
        today: 0,
        this_month: 0,
        total: 0,
        total_amount: 0
    },
    withdrawals: {
        today: 0,
        total: 0,
        this_month: 0,
        total_amount: 0
    },
    commissions: {
        total: 0,
        total_amount: 0
    },
    loans: {
        today: 0,
        total: 0,
        total_loans_principal: 0
    }
}